import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule } from '@client/app/modules/translate.module';
import { MonActionMenuModule } from '@client/ng2/shared/components/action-menu/action-menu.module';
import { Domain } from '@monsido/modules/models/api/domain';
import { MON_MODULES } from '@monsido/core/constants/mon-modules.constant';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { MonModalService } from '@client/app/modules/modals/services/mon-modal/mon-modal.service';
import { ActiveFeatureService } from '@monsido/ng2/services/active-feature/active-feature.service';
import { User } from '@monsido/modules/models/api/user';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { ActionMenuItemType } from '@client/ng2/shared/components/action-menu-panel/action-menu-panel.type';

@Component({
    selector: 'domain-action-menu-button',
    template: `
        @if (updating()) {
            <span class="fas fa-spinner fa-spin fa-3x"></span>
        } @else {
            <mon-action-menu [monActionMenuItems]="dropdowns()">
                <span translate>Action</span>
            </mon-action-menu>
        }
    `,
    standalone: true,
    imports: [
        MonActionMenuModule,
        TranslateModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})

export class DomainActionMenuButtonComponent {
    translateService = inject(TranslateService);
    monModalService = inject(MonModalService);
    activeFeatureService = inject(ActiveFeatureService);
    pipeService = inject(PipeService);

    updating = input<boolean | undefined>(false);
    domain = input<Domain>();
    user = input<User | null | undefined>();
    hasOnDemandScans = input(false);
    scriptSetupGuideIsAvailable = input(false);

    goToDomain = output();
    openScriptGuide = output();
    editDomain = output();
    editExcludedIps = output();
    startOnDemandScan = output();
    cloneDomain = output();
    openDataPrivacy = output();
    updateDomainGroup = output();
    confirmDelete = output();
    stopCurrentCrawl = output();

    dropdowns = computed<ActionMenuItemType[]>((): ActionMenuItemType[] => {
        const canStopCrawl = this.canStopCrawlForDomain(this.domain()?.crawl_status?.step);
        const hasDomainGroups = (this.domain()?.domain_groups?.length || 0) > 0;

        return [
            {
                label: this.translateService.getString('Go to the dashboard'),
                leftIcon: 'faHome',
                action: () => this.goToDomain.emit(),
                eventParams: { action: 'admin-domain-dashboard' },
                dataTestAttribute: 'go-to-dashboard-button',
            },
            {
                label: this.translateService.getString('Script setup guide'),
                leftIcon: 'faCode',
                action: () => this.openScriptGuide.emit(),
                eventParams: { action: 'admin-domain-script-setup' },
                shouldShow: this.scriptSetupGuideIsAvailable(),
                dataTestAttribute: 'script-setup-button',
            },
            {
                label: this.translateService.getString('Edit domain'),
                leftIcon: 'faCog',
                action: () => this.editDomain.emit(),
                eventParams: { action: 'admin-domain-edit' },
                dataTestAttribute: 'edit-domain-button',
            },
            {
                label: this.translateService.getString('Statistics excluded IP addresses'),
                leftIcon: 'faChartLine',
                action: () => this.editExcludedIps.emit(),
                eventParams: { action: 'admin-domain-excluded-ip' },
                shouldShow: this.domain()?.features.statistics === true,
                dataTestAttribute: 'exclude-ips-button',
            },
            {
                label: this.translateService.getString('Start on demand scan'),
                leftIcon: 'faSyncAlt',
                action: () => this.startOnDemandScan.emit(),
                eventParams: { action: 'admin-domain-start-demand-scan' },
                disabled: !this.hasOnDemandScans() || Boolean(this.domain()?.crawl_status),
                tooltip: this.hasOnDemandScans() ? ''
                    : this.translateService.getString(
                        'Your current plan does not include on-demand scans. ' +
                        'Please reach out to your Acquia Web Governance representative to learn more about this option',
                    ),
                dataTestAttribute: 'start-on-demand-scan-button',
            },
            {
                label: this.translateService.getString('Clone'),
                leftIcon: 'faClone',
                action: () => this.cloneDomain.emit(),
                eventParams: { action: 'admin-domain-clone' },
                dataTestAttribute: 'clone-domain-button',
            },
            {
                label: this.translateService.getString('Open Data Privacy'),
                action: () => this.openDataPrivacy.emit(),
                shouldShow: this.activeFeatureService.isFeatureActive(MON_MODULES.data_privacy, this.domain()) && this.pipeService.get('hasAccess')(this.user(), MON_MODULES.data_privacy),
                dataTestAttribute: 'open-data-privacy-button',
            },
            {
                divider: true,
                shouldShow: hasDomainGroups,
            },
            {
                label: this.translateService.getString('Update all domain groups'),
                leftIcon: 'faRedoAlt',
                rightIcon: this.domain()?.running_groups ? 'faSpinner' : '',
                spinRightIcon: true,
                action: () => {
                    const message = this.translateService.getString(
                        'Updating groups can take more than 30 minutes. ' +
                        'During the operation you will not be able to create and update groups.',
                    );
                    this.monModalService.confirm(message, {}).then(() => this.updateDomainGroup.emit());
                },
                disabled: this.domain()?.running_groups ?? false,
                shouldShow: hasDomainGroups,
                dataTestAttribute: 'update-domain-group-button',
            },
            {
                divider: true,
            },
            {
                label: this.translateService.getString('Remove domain'),
                leftIcon: 'faTrashAlt',
                action: () => this.confirmDelete.emit(),
                eventParams: { action: 'admin-domain-remove' },
                dataTestAttribute: 'delete-domain-button',
            },
            {
                divider: true,
                shouldShow: canStopCrawl,
            },
            {
                label: this.translateService.getString('Stop current crawl'),
                leftIcon: 'faStopCircle',
                action: () => {
                    const message = this.translateService.getString('Are you sure you want to stop current crawl?');
                    this.monModalService.confirm(message, {}).then(() => this.stopCurrentCrawl.emit());
                },
                eventParams: { action: 'admin-domain-stop-crawl' },
                shouldShow: canStopCrawl,
                dataTestAttribute: 'stop-current-crawl-button',
            },
        ];
    });

    stoppableStatuses = ['started', 'queued', 'backoff', 'retry'];


    private canStopCrawlForDomain (crawlStatusStep?: string): boolean {
        if (!crawlStatusStep) {
            return false;
        }
        return this.stoppableStatuses.includes(crawlStatusStep);
    }
}
